import { Controller } from "@hotwired/stimulus";

// Store focus state globally so it persists across controller reconnections
let pendingFocusRestore: { cursorPos: number } | null = null;

export default class RequestFilterController extends Controller {
  static targets = ["form", "searchInput", "searchIcon", "loadingIndicator"];

  declare readonly formTarget: HTMLFormElement;
  declare readonly searchInputTarget: HTMLInputElement;
  declare readonly searchIconTarget: HTMLElement;
  declare readonly loadingIndicatorTarget: HTMLElement;
  declare readonly hasFormTarget: boolean;
  declare readonly hasSearchInputTarget: boolean;
  declare readonly hasSearchIconTarget: boolean;
  declare readonly hasLoadingIndicatorTarget: boolean;

  private debounceTimer: number | null = null;
  private readonly DEBOUNCE_DELAY = 300;
  private boundHideLoading: () => void;

  connect(): void {
    // Bind the hide loading handler for Turbo events
    this.boundHideLoading = this.hideLoading.bind(this);
    document.addEventListener("turbo:frame-load", this.boundHideLoading);

    // Restore focus if we have a pending restore (after Turbo frame update)
    if (pendingFocusRestore && this.hasSearchInputTarget) {
      requestAnimationFrame(() => {
        if (pendingFocusRestore && this.hasSearchInputTarget) {
          this.searchInputTarget.focus();
          const pos = Math.min(
            pendingFocusRestore.cursorPos,
            this.searchInputTarget.value.length
          );
          this.searchInputTarget.setSelectionRange(pos, pos);
          pendingFocusRestore = null;
        }
      });
    }

    // Ensure loading state is hidden on connect (fresh page load)
    this.hideLoading();
  }

  disconnect(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    document.removeEventListener("turbo:frame-load", this.boundHideLoading);
  }

  // Debounced search for text input
  debounceSearch(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = window.setTimeout(() => {
      this.submit();
    }, this.DEBOUNCE_DELAY);
  }

  // Immediate submit for select/date changes
  submit(): void {
    if (!this.hasFormTarget) return;

    // Save focus state before submission so it can be restored after Turbo replaces content
    if (
      this.hasSearchInputTarget &&
      document.activeElement === this.searchInputTarget
    ) {
      pendingFocusRestore = {
        cursorPos:
          this.searchInputTarget.selectionStart ||
          this.searchInputTarget.value.length,
      };
    }

    this.showLoading();
    this.formTarget.requestSubmit();
  }

  // Clear search input
  clearSearch(): void {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = "";
      this.submit();
    }
  }

  private showLoading(): void {
    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.add("hidden");
    }
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove("hidden");
    }
  }

  private hideLoading(): void {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add("hidden");
    }
    if (this.hasSearchIconTarget) {
      this.searchIconTarget.classList.remove("hidden");
    }
  }
}
